import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../utils/api';
import styles from './WatchlistPage.module.css';
import { formatSymbol } from '../../utils/symbolFormatter';
import { usePrice } from '../../context/PriceContext';

import { FaChartLine, FaTrash, FaRegBookmark } from 'react-icons/fa';
import { FiSearch, FiPlus } from 'react-icons/fi';

const getExchange = (symbol) => {
  if (symbol.symbol_type === 'F&O') {
    const userSymbol = symbol.user_symbol.toUpperCase();
    if (userSymbol.startsWith('NIFTY') || userSymbol.startsWith('BANKNIFTY')) {
      return 'NSE';
    } else if (userSymbol.startsWith('SENSEX') || userSymbol.startsWith('BANKEX')) {
      return 'BSE';
    }
  }
  return 'NSE';
};

const WatchlistPage = () => {
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]); // Array of {name, symbols}
  const [activeTabName, setActiveTabName] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const searchContainerRef = useRef(null);

  const activeWatchlist = watchlists.find(w => w.name === activeTabName) || { symbols: [] };

  const fetchAllWatchlists = useCallback(async () => {
    try {
      const { data } = await api.getWatchlists();
      setWatchlists(data);
      if (data.length > 0 && !activeTabName) {
        setActiveTabName(data[0].name);
      } else if (data.length > 0 && !data.find(w => w.name === activeTabName)) {
        setActiveTabName(data[0].name);
      }
    } catch (err) { console.error(err); }
  }, [activeTabName]);

  useEffect(() => { fetchAllWatchlists(); }, [fetchAllWatchlists]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSearch = async () => {
        if (searchQuery.trim().length >= 2) {
            try {
                const { data } = await api.getSymbols();
                const filtered = data.filter(s => 
                    s.user_symbol.toLowerCase().includes(searchQuery.toLowerCase())
                ).slice(0, 10);
                setSearchResults(filtered);
            } catch (e) { console.error(e); }
        } else setSearchResults([]);
    };
    const timer = setTimeout(fetchSearch, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCreateWatchlist = async () => {
    if (watchlists.length >= 3) return alert("Limit: Maximum 3 watchlists allowed");
    const name = window.prompt("Enter watchlist name:");
    if (!name || name.trim().length === 0) return;
    try {
        const { data } = await api.createWatchlist(name.trim());
        setWatchlists(data);
        setActiveTabName(name.trim());
    } catch (err) { alert(err.response?.data?.msg || "Creation failed"); }
  };

  const handleDeleteWatchlist = async (name) => {
    if (!window.confirm(`Delete watchlist "${name}"?`)) return;
    try {
        const { data } = await api.deleteWatchlist(name);
        setWatchlists(data);
    } catch (err) { console.error(err); }
  };

  const handleAddToWatchlist = async (item) => {
    try {
      await api.addToWatchlist(item.user_symbol, activeTabName);
      fetchAllWatchlists();
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      alert(err.response?.data?.msg || "Add failed");
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    try {
      await api.removeFromWatchlist(symbol, activeTabName);
      fetchAllWatchlists();
    } catch (err) { console.error(err); }
  };

  return (
    <div className={styles['watchlist-page']}>
      <div className={styles['watchlist-header']}>
        <div className={styles['header-top']}>
            <h1>Watchlist</h1>
            <div className={styles['count-group']}>
                <span className={styles.count}>{activeWatchlist.symbols.length}/20</span>
                {activeTabName && <button className={styles.clearBtn} onClick={() => handleDeleteWatchlist(activeTabName)}>Delete</button>}
            </div>
        </div>

        <div className={styles['search-bar']} ref={searchContainerRef}>
            <FiSearch className={styles['search-icon']} />
            <input type="text" placeholder="Search and add symbols..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoComplete="off" />
            {searchResults.length > 0 && (
            <ul className={styles['search-results']}>
                {searchResults.map((item) => (
                <li key={`${item.user_symbol}-${item.sr_no}`} onClick={() => handleAddToWatchlist(item)}>
                    <div className={styles.resInfo}>
                        <span className={styles.resSymbol}>{formatSymbol(item.user_symbol)}</span>
                        <div className={styles.resSubRow}>
                            <span className={styles.resExch}>{getExchange(item)}</span>
                            <span className={styles.resType}>{item.symbol_type}</span>
                        </div>
                    </div>
                    <FiPlus className={styles.addIcon}/>
                </li>
                ))}
            </ul>
            )}
        </div>

        <div className={styles.watchlistSelector}>
            <div className={styles.chipsContainer}>
                {watchlists.map((w) => (
                    <div 
                        key={w.name} 
                        className={`${styles.watchlistChip} ${activeTabName === w.name ? styles.activeChip : ''}`}
                        onClick={() => setActiveTabName(w.name)}
                    >
                        <span className={styles.chipName}>{w.name}</span>
                    </div>
                ))}
                {watchlists.length < 3 && (
                    <div className={styles.addChip} onClick={handleCreateWatchlist}>
                        <FiPlus />
                    </div>
                )}
            </div>
        </div>
      </div>

      <div className={styles.watchlist}>
        {activeWatchlist.symbols.length === 0 ? (
            <div className={styles['empty-state']}>
                <FaRegBookmark className={styles['empty-icon']} />
                <p>Your watchlist is empty.<br/>Search symbols above to add them.</p>
            </div>
        ) : (
            activeWatchlist.symbols.map((symbol) => (
                <WatchlistRow
                    key={symbol}
                    symbol={symbol}
                    onRemove={() => handleRemoveFromWatchlist(symbol)}
                    onOrder={(type, lp) => navigate(`/order/${symbol}/${type}`, { state: { livePrice: lp } })}
                    onChart={() => navigate('/trade', { state: { chartSymbol: symbol } })}
                />
            ))
        )}
      </div>
    </div>
  );
};

const WatchlistRow = memo(({ symbol, onRemove, onOrder, onChart }) => {
    const [showOptions, setShowOptions] = useState(false);
    const { price: livePrice, lastDayClose: close } = usePrice(symbol);
    
    if (!symbol) return null;

    const diff = (livePrice && close) ? livePrice - close : 0;
    const pct = close ? (diff / close) * 100 : 0;

    return (
      <div className={styles['watchlist-row']} onClick={() => setShowOptions(!showOptions)}>
        <div className={styles['row-main-content']}>
            <div className={styles['info-group']} >
                <p className={styles['watchlist-name']}>{formatSymbol(symbol)}</p>
                <span className={styles['watchlist-exch']}>NSE</span>
            </div>
            <div className={styles['price-group']}>
                <p className={styles['watchlist-price']}>₹{livePrice?.toFixed(2) || '--'}</p>
                <p className={`${styles['watchlist-change']} ${diff >= 0 ? styles.positive : styles.negative}`}>
                    {diff.toFixed(2)} ({pct.toFixed(2)}%)
                </p>
            </div>
        </div>
        
        {showOptions && (
            <div className={styles['actions-group']}>
                <button className={styles.buyBtn} onClick={(e) => { e.stopPropagation(); onOrder('buy', livePrice); }}>BUY</button>
                <button className={styles.sellBtn} onClick={(e) => { e.stopPropagation(); onOrder('sell', livePrice); }}>SELL</button>
                <button className={styles.actionIcon} onClick={(e) => { e.stopPropagation(); onChart(); }}><FaChartLine /></button>
                <button className={`${styles.actionIcon} ${styles.delete}`} onClick={(e) => { e.stopPropagation(); onRemove(); }}><FaTrash /></button>
            </div>
        )}
      </div>
    );
});

export default WatchlistPage;
